import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { SHEET_TOKENS, getGlassSurface, getBackdropStyle } from '../../pages/MojeSmena/sheetStyle';
import useJeMobil from '../../hooks/useJeMobil';

/**
 * Obal modalu — NORMA. Řeší tři věci, na kterých se to rozbíjelo pokaždé znovu:
 *
 * 1. **Portál do `body`.** `position: fixed` se neváže na výřez, ale na nejbližšího
 *    předka s `transform`, `filter` nebo `backdrop-filter`. Karty modulů sklo mají
 *    (`cardStyle` má `backdrop-filter: blur(40px)`), takže modal vykreslený uvnitř
 *    karty se pozicuje i ořezává podle ní — vyjde mimo střed a schová se pod
 *    hlavičku. Portál je jediná spolehlivá obrana.
 * 2. **Na mobilu zdola, na desktopu na střed.** Na telefonu je modal plátno u dolní
 *    hrany, kam dosáhne palec, a zaoblené má jen horní rohy. Vycentrovaná kartička
 *    s okrajem kolem dokola je rozvržení pro myš.
 * 3. **Nad mobilní lištou.** Lišta jede na `zIndex` 58–60; modal, který se dostane
 *    pod ni, má spodní hranu překrytou plovoucí lištou přesně tam, kde bývá Uložit.
 *    Výchozí `zIndex` je proto o řád výš a `Z_NAD_LISTOU` říká, kde je dno.
 *
 * Barvy si nebere vlastní — jsou to tokeny z `pages/MojeSmena/sheetStyle`, tedy
 * totéž sklo jako mají sheety v Přehledu. Dřív si každý modal míchal podklad sám
 * a `rgba(26,26,38)` proti tamnímu `rgba(24,22,32)` táhlo do modra.
 */
export const Z_NAD_LISTOU = 1100;

export default function ModalShell({
    open = true,
    onClose,
    isDark = false,
    nadpis,
    ikona: Ikona = null,
    barva = '#F59E0B',
    paticka = null,
    sirka = 460,
    zIndex = Z_NAD_LISTOU,
    zavritLze = true,
    children,
}) {
    const isMobile = useJeMobil();

    const zavri = () => { if (zavritLze) onClose?.(); };

    // Pozadí pod modalem nesmí scrollovat — jinak se na dotyku hýbe stránka
    // za ním místo obsahu modalu.
    useEffect(() => {
        if (!open) return;
        const predtim = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = predtim; };
    }, [open]);

    // Počet otevřených modalů — tlačítko Zpět v mobilní aplikaci podle něj
    // nejdřív zavře modal (pošle Escape), teprve pak jde zpět v historii.
    useEffect(() => {
        if (!open) return;
        window.__flOtevreneModaly = (window.__flOtevreneModaly || 0) + 1;
        return () => { window.__flOtevreneModaly = Math.max(0, (window.__flOtevreneModaly || 1) - 1); };
    }, [open]);
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') zavri(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    });

    if (!open || typeof document === 'undefined') return null;

    const sklo = getGlassSurface(isDark);
    const hranice = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.50)' : 'var(--text-muted, #94a3b8)';
    const R = SHEET_TOKENS.radius;

    return createPortal((
        <div role="dialog" aria-modal="true" aria-label={typeof nadpis === 'string' ? nadpis : undefined}
            onClick={zavri}
            style={{
                ...getBackdropStyle(), zIndex,
                flexDirection: 'row', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
                padding: isMobile ? 0 : 16,
            }}>
            <style>{`
                @keyframes modal-shell-zdola { from { transform: translateY(14px); opacity: 0 } to { transform: none; opacity: 1 } }
                @media (prefers-reduced-motion: reduce) { .modal-shell { animation: none !important } }
            `}</style>

            <div className="modal-shell" onClick={e => e.stopPropagation()}
                style={{
                    ...sklo,
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    width: isMobile ? '100%' : sirka,
                    maxWidth: '100%',
                    // dvh, ne vh — na mobilu se lišta prohlížeče schovává a `vh`
                    // pak modal přetáhne pod spodní hranu výřezu
                    maxHeight: isMobile ? '88dvh' : '85vh',
                    borderRadius: isMobile ? `${R.container}px ${R.container}px 0 0` : R.containerDesktop,
                    ...(isMobile ? { borderBottom: 'none' } : null),
                    animation: 'modal-shell-zdola 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}>

                {nadpis && (
                    <div className="flex items-center justify-between" style={{
                        padding: isMobile ? '12px 12px 12px 16px' : '14px 12px 14px 16px',
                        borderBottom: `1px solid ${hranice}`, flexShrink: 0,
                    }}>
                        <h3 className="text-sm font-semibold flex items-center gap-2 min-w-0" style={{ color: 'var(--text-primary)' }}>
                            {Ikona && <Ikona size={15} style={{ color: barva, flexShrink: 0 }} />}
                            <span className="truncate">{nadpis}</span>
                        </h3>
                        {zavritLze && (
                            <button onClick={onClose} aria-label="Zavřít"
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer', color: mutedColor,
                                    width: isMobile ? 44 : 36, height: isMobile ? 44 : 36, flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    WebkitTapHighlightColor: 'transparent',
                                }}>
                                <X size={18} />
                            </button>
                        )}
                    </div>
                )}

                {/* `overflowX: hidden` je pojistka, ne řešení. `overflow-y: auto` sám
                    udělá z `overflow-x` taky `auto`, takže jediné moc široké pole
                    rozjede tělo modalu do boku — a to se pozná až na telefonu, protože
                    šířka dokumentu zůstane v pořádku. Ať se to schová, než aby modal
                    ujel; příčinu je ale pořád potřeba spravit u pole samotného. */}
                <div className="overflow-y-auto" style={{ flex: 1, minHeight: 0, overflowX: 'hidden', overscrollBehavior: 'contain' }}>
                    {children}
                </div>

                {paticka && (
                    <div style={{
                        padding: isMobile ? 12 : 16, borderTop: `1px solid ${hranice}`, flexShrink: 0,
                        // Domovské gesto iPhonu sedí přesně tam, kde jsou potvrzovací
                        // tlačítka — bez toho se Uložit maže s tahem pro zavření aplikace.
                        ...(isMobile ? { paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' } : null),
                    }}>
                        {paticka}
                    </div>
                )}
            </div>
        </div>
    ), document.body);
}
